'use client';

import { stack, css, ConditionalValue } from '@bts-web/utils-style-engine';
import { DialogProps } from '@bts-web/design-system/component/dialog';
import { useRouter } from 'next/navigation';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import {
  AccessInfoType,
  getAccessModalInfo,
  getUserAccessModalTranslations,
} from '../../../utils/userAccess';
import { DialogBase } from '../../DialogBase/DialogBase';
import { ButtonBase } from '../../ButtonBase/ButtonBase';
import { ModalComponent } from '../../ModalDrawerContext/ModalDrawerContext';

export type DialogComponentProps = Omit<
  DialogProps['external'],
  'closeIcon'
> & {
  visual?: ConditionalValue<'primary' | 'secondary'> | undefined;
  size?: ConditionalValue<'small' | 'fullscreen'> | undefined;
};

const modalWrapStyles = stack({
  gap: 'large',
  alignItems: 'center',
  textAlign: 'center',
});

const messageStyles = css({
  color: 'neutrals.text_secondary',
  fontSize: 'body.medium',
  fontWeight: 'body.medium',
  letterSpacing: 'body.medium',
  lineHeight: 'body.medium',
  pt: 'small',
});

export type UserAccessModalProps = {
  type: AccessInfoType;
  onModalClose?: () => void;
};

export const UserAccessModal: ModalComponent<UserAccessModalProps> = ({
  type,
  onClose,
  onModalClose,
}) => {
  const router = useRouter();

  const { t } = useClientTranslation();

  const translations = getUserAccessModalTranslations(
    t as (key: string) => string,
  );

  const { icon, title, message, action } = getAccessModalInfo(
    translations,
    type,
  );

  const handleClick = () => {
    onClose();

    if (onModalClose) {
      onModalClose();
    }

    if (action.to) {
      router.push(action?.to);
    }
  };

  return (
    <DialogBase
      id="base-modal"
      onClose={onClose}
      size="small"
      visual="secondary"
      title={title}
      showTitleBelowHeader
      contentProps={{ disableTitlePadding: true, disableTitleGap: true }}
      aboveTitleElement={icon}
    >
      <div className={modalWrapStyles}>
        <div className={messageStyles}>{message}</div>

        {action &&
          (action?.component || (
            <div
              className={css({
                maxWidth: '342px',
                width: '100%',
              })}
            >
              <ButtonBase onClick={handleClick} visual="primary">
                {action?.label}
              </ButtonBase>
            </div>
          ))}
      </div>
    </DialogBase>
  );
};
